import * as Cesium from "cesium";

// ImprovedNoise from Three.js for wind field generation
// https://github.com/mrdoob/three.js/blob/dev/examples/jsm/math/ImprovedNoise.js

const lerp = Cesium.Math.lerp;

const _p = [
  151, 160, 137, 91, 90, 15, 131, 13, 201, 95, 96, 53, 194, 233, 7, 225, 140,
  36, 103, 30, 69, 142, 8, 99, 37, 240, 21, 10, 23, 190, 6, 148, 247, 120, 234,
  75, 0, 26, 197, 62, 94, 252, 219, 203, 117, 35, 11, 32, 57, 177, 33, 88, 237,
  149, 56, 87, 174, 20, 125, 136, 171, 168, 68, 175, 74, 165, 71, 134, 139, 48,
  27, 166, 77, 146, 158, 231, 83, 111, 229, 122, 60, 211, 133, 230, 220, 105,
  92, 41, 55, 46, 245, 40, 244, 102, 143, 54, 65, 25, 63, 161, 1, 216, 80, 73,
  209, 76, 132, 187, 208, 89, 18, 169, 200, 196, 135, 130, 116, 188, 159, 86,
  164, 100, 109, 198, 173, 186, 3, 64, 52, 217, 226, 250, 124, 123, 5, 202, 38,
  147, 118, 126, 255, 82, 85, 212, 207, 206, 59, 227, 47, 16, 58, 17, 182, 189,
  28, 42, 223, 183, 170, 213, 119, 248, 152, 2, 44, 154, 163, 70, 221, 153, 101,
  155, 167, 43, 172, 9, 129, 22, 39, 253, 19, 98, 108, 110, 79, 113, 224, 232,
  178, 185, 112, 104, 218, 246, 97, 228, 251, 34, 242, 193, 238, 210, 144, 12,
  191, 179, 162, 241, 81, 51, 145, 235, 249, 14, 239, 107, 49, 192, 214, 31,
  181, 199, 106, 157, 184, 84, 204, 176, 115, 121, 50, 45, 127, 4, 150, 254,
  138, 236, 205, 93, 222, 114, 67, 29, 24, 72, 243, 141, 128, 195, 78, 66, 215,
  61, 156, 180,
];

for (let i = 0; i < 256; i++) {
  _p[256 + i] = _p[i];
}

function fade(t) {
  return t * t * t * (t * (t * 6 - 15) + 10);
}

function grad(hash, x, y, z) {
  const h = hash & 15;
  const u = h < 8 ? x : y,
    v = h < 4 ? y : h === 12 || h === 14 ? x : z;
  return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
}

class ImprovedNoise {
  noise(x, y, z) {
    const floorX = Math.floor(x),
      floorY = Math.floor(y),
      floorZ = Math.floor(z);

    const X = floorX & 255,
      Y = floorY & 255,
      Z = floorZ & 255;

    x -= floorX;
    y -= floorY;
    z -= floorZ;

    const xMinus1 = x - 1,
      yMinus1 = y - 1,
      zMinus1 = z - 1;

    const u = fade(x),
      v = fade(y),
      w = fade(z);

    const A = _p[X] + Y,
      AA = _p[A] + Z,
      AB = _p[A + 1] + Z,
      B = _p[X + 1] + Y,
      BA = _p[B] + Z,
      BB = _p[B + 1] + Z;

    return lerp(
      lerp(
        lerp(grad(_p[AA], x, y, z), grad(_p[BA], xMinus1, y, z), u),
        lerp(grad(_p[AB], x, yMinus1, z), grad(_p[BB], xMinus1, yMinus1, z), u),
        v,
      ),
      lerp(
        lerp(
          grad(_p[AA + 1], x, y, zMinus1),
          grad(_p[BA + 1], xMinus1, y, zMinus1),
          u,
        ),
        lerp(
          grad(_p[AB + 1], x, yMinus1, zMinus1),
          grad(_p[BB + 1], xMinus1, yMinus1, zMinus1),
          u,
        ),
        v,
      ),
      w,
    );
  }
}

/**
 * Generate a 3D wind field using Perlin noise
 * @param {Object} params - Wind field parameters
 * @returns {Object} Wind field data structure
 */
function generateWindField(params) {
  const {
    baseSpeed = 10.0,      // Base wind speed in m/s
    baseDirection = 45.0,  // Base direction in degrees
    turbulence = 0.3,      // Turbulence intensity (0-1)
    gridSize = 32,         // Grid resolution (32x32x4)
    altitudeLevels = 4,    // Number of altitude levels
  } = params;

  const noise = new ImprovedNoise();
  const windField = [];

  // Generate wind vectors at each grid point
  for (let z = 0; z < altitudeLevels; z++) {
    for (let y = 0; y < gridSize; y++) {
      for (let x = 0; x < gridSize; x++) {
        // Normalized coordinates (0-1)
        const nx = x / gridSize;
        const ny = y / gridSize;
        const nz = z / altitudeLevels;

        // Sample Perlin noise for variation
        const noiseScale = 3.0;
        const noiseX = noise.noise(nx * noiseScale, ny * noiseScale, nz * noiseScale + 100);
        const noiseY = noise.noise(nx * noiseScale, ny * noiseScale, nz * noiseScale + 200);
        const noiseZ = noise.noise(nx * noiseScale, ny * noiseScale, nz * noiseScale + 300);

        // Base wind vector from direction and speed
        const dirRad = Cesium.Math.toRadians(baseDirection);
        const baseU = Math.cos(dirRad) * baseSpeed;
        const baseV = Math.sin(dirRad) * baseSpeed;

        // Add turbulence
        const u = baseU + noiseX * baseSpeed * turbulence;
        const v = baseV + noiseY * baseSpeed * turbulence;
        const w = noiseZ * baseSpeed * turbulence * 0.3; // Less vertical turbulence

        windField.push({ x, y, z, u, v, w });
      }
    }
  }

  return {
    field: windField,
    gridSize,
    altitudeLevels,
    baseSpeed,
    baseDirection,
  };
}

/**
 * Sample wind velocity from the wind field grid
 * @param {Object} windData - Wind field data
 * @param {Cesium.Cartesian3} localPosition - Position in local coordinates (normalized -0.5 to 0.5)
 * @returns {Cesium.Cartesian3} Wind velocity vector
 */
function sampleWindField(windData, localPosition) {
  const { field, gridSize, altitudeLevels } = windData;

  // Convert from local box space (-0.5 to 0.5) to grid indices (0 to gridSize-1)
  const gx = (localPosition.x + 0.5) * (gridSize - 1);
  const gy = (localPosition.y + 0.5) * (gridSize - 1);
  const gz = (localPosition.z + 0.5) * (altitudeLevels - 1);

  // Clamp to valid range
  const x0 = Math.max(0, Math.min(gridSize - 2, Math.floor(gx)));
  const y0 = Math.max(0, Math.min(gridSize - 2, Math.floor(gy)));
  const z0 = Math.max(0, Math.min(altitudeLevels - 2, Math.floor(gz)));

  const x1 = x0 + 1;
  const y1 = y0 + 1;
  const z1 = z0 + 1;

  // Interpolation weights
  const fx = gx - x0;
  const fy = gy - y0;
  const fz = gz - z0;

  // Get 8 corner samples (trilinear interpolation)
  const getField = (x, y, z) => {
    const idx = z * gridSize * gridSize + y * gridSize + x;
    return field[idx] || { u: 0, v: 0, w: 0 };
  };

  const c000 = getField(x0, y0, z0);
  const c100 = getField(x1, y0, z0);
  const c010 = getField(x0, y1, z0);
  const c110 = getField(x1, y1, z0);
  const c001 = getField(x0, y0, z1);
  const c101 = getField(x1, y0, z1);
  const c011 = getField(x0, y1, z1);
  const c111 = getField(x1, y1, z1);

  // Trilinear interpolation for each component
  const u = lerp(
    lerp(lerp(c000.u, c100.u, fx), lerp(c010.u, c110.u, fx), fy),
    lerp(lerp(c001.u, c101.u, fx), lerp(c011.u, c111.u, fx), fy),
    fz
  );

  const v = lerp(
    lerp(lerp(c000.v, c100.v, fx), lerp(c010.v, c110.v, fx), fy),
    lerp(lerp(c001.v, c101.v, fx), lerp(c011.v, c111.v, fx), fy),
    fz
  );

  const w = lerp(
    lerp(lerp(c000.w, c100.w, fx), lerp(c010.w, c110.w, fx), fy),
    lerp(lerp(c001.w, c101.w, fx), lerp(c011.w, c111.w, fx), fy),
    fz
  );

  return new Cesium.Cartesian3(u, v, w);
}

/**
 * Initialise wind visualisation over Liège, Belgium
 * @param {Cesium.Viewer} viewer - The Cesium viewer instance
 * @param {Object} params - Wind parameters
 * @param {number} params.baseSpeed - Base wind speed in m/s (default: 10.0)
 * @param {number} params.baseDirection - Wind direction in degrees (default: 45.0)
 * @param {number} params.particleDensity - Number of particles (default: 2000)
 * @param {number} params.altitude - Centre altitude in metres (default: 1000)
 * @param {number} params.turbulence - Turbulence intensity 0-1 (default: 0.3)
 * @returns {Object} Wind primitive, uniforms, and wind field data
 */
export function initialiseWind(viewer, params = {}) {
  // Default parameters
  const defaultParams = {
    baseSpeed: 10.0,
    baseDirection: 45.0,
    particleDensity: 2000,
    altitude: 1000,
    turbulence: 0.3,
  };

  const windParams = { ...defaultParams, ...params };

  // Generate wind field
  const windData = generateWindField({
    baseSpeed: windParams.baseSpeed,
    baseDirection: windParams.baseDirection,
    turbulence: windParams.turbulence,
  });

  // Liège centre (same as volumetric clouds)
  const liegeCenter = Cesium.Cartesian3.fromDegrees(
    5.6185, // Centre longitude
    50.6332, // Centre latitude
    windParams.altitude, // Configurable altitude
  );

  // Wind volume dimensions (match cloud volume for consistency)
  const windWidth = 15000;   // 15km wide (east-west)
  const windDepth = 15000;   // 15km deep (north-south)
  const windHeight = 2000;   // 2km tall (vertical)

  // Create model matrix for local coordinate frame
  const modelMatrix = Cesium.Transforms.eastNorthUpToFixedFrame(liegeCenter);

  // Scale matrix to convert local coordinates to world space
  const scaleMatrix = Cesium.Matrix4.fromScale(
    new Cesium.Cartesian3(windWidth, windDepth, windHeight),
    new Cesium.Matrix4()
  );

  const finalModelMatrix = Cesium.Matrix4.multiply(
    modelMatrix,
    scaleMatrix,
    new Cesium.Matrix4()
  );

  // Particle emitter - use local box coordinates (0-1 range)
  // Emit from a box covering 80% of the volume
  const emitterSize = 0.4; // 0.4 in local space = 80% of volume width

  // Debug counter for update callback
  let updateCallCount = 0;

  // Simple wind velocity in ENU coordinates (East-North-Up)
  // Direction in degrees, converted to radians
  const dirRad = Cesium.Math.toRadians(windParams.baseDirection);
  const windVelocityENU = new Cesium.Cartesian3(
    Math.cos(dirRad) * windParams.baseSpeed,  // East
    Math.sin(dirRad) * windParams.baseSpeed,  // North
    0.0  // Up
  );

  // Define particle size outside the constructor (like rain does)
  const windParticleSize = 30.0;
  const windImageSize = new Cesium.Cartesian2(windParticleSize, windParticleSize * 2.0);

  // Create particle system - try WITHOUT update callback first
  const windParticleSystem = new Cesium.ParticleSystem({
    show: true,
    loop: true,
    modelMatrix: modelMatrix,
    minimumSpeed: 5.0,
    maximumSpeed: 15.0,
    lifetime: 20.0,
    emitter: new Cesium.SphereEmitter(10000.0),  // Try sphere emitter like rain
    startScale: 5.0,
    endScale: 3.0,
    image: "assets/circular_particle.png",
    emissionRate: 1000.0,  // Very high rate
    startColor: new Cesium.Color(1.0, 0.0, 0.0, 1.0),  // Bright red
    endColor: new Cesium.Color(1.0, 1.0, 0.0, 1.0),  // Bright yellow (don't fade to test)
    imageSize: windImageSize,
  });

  // Log particle system state after a short delay
  setTimeout(() => {
    console.log('Particle system after 1 second:', {
      show: windParticleSystem.show,
      isComplete: windParticleSystem.isComplete,
      numParticles: windParticleSystem._particles ? windParticleSystem._particles.length : 0,
    });
  }, 1000);

  console.log('Wind particle system created:', {
    emissionRate: windParticleSystem.emissionRate,
    lifetime: windParticleSystem.lifetime,
    imageSize: windParticleSystem.imageSize,
    startColor: windParticleSystem.startColor,
    centerPoint: liegeCenter,
    dimensions: { width: windWidth, depth: windDepth, height: windHeight },
  });

  return {
    primitive: windParticleSystem,
    windData,
    centerPoint: liegeCenter,
    dimensions: { width: windWidth, depth: windDepth, height: windHeight },
  };
}

/**
 * Update wind parameters and regenerate wind field
 * @param {Cesium.ParticleSystem} particleSystem - The wind particle system
 * @param {Object} windData - Current wind data
 * @param {Object} params - Parameters to update
 * @returns {Object} Updated wind data
 */
export function updateWindParameters(particleSystem, windData, params) {
  let newWindData = windData;

  // If wind field parameters changed, regenerate the field
  if (
    params.baseSpeed !== undefined ||
    params.baseDirection !== undefined ||
    params.turbulence !== undefined
  ) {
    newWindData = generateWindField({
      baseSpeed: params.baseSpeed ?? windData.baseSpeed,
      baseDirection: params.baseDirection ?? windData.baseDirection,
      turbulence: params.turbulence ?? windData.turbulence,
    });
  }

  // Update particle system parameters
  if (params.particleDensity !== undefined) {
    particleSystem.emissionRate = params.particleDensity / 20.0;
  }

  return newWindData;
}

/**
 * Fly camera to view wind over Liège
 * @param {Cesium.Viewer} viewer - The Cesium viewer instance
 * @param {Cesium.Cartesian3} centerPoint - Wind centre point
 */
export function flyToWindView(viewer, centerPoint) {
  const windWidth = 15000; // Match the wind dimensions

  // Position camera at a distance to view the entire wind volume
  const distance = windWidth * 2; // 2x the width for good viewing angle

  viewer.camera.flyTo({
    destination: centerPoint,
    orientation: {
      heading: Cesium.Math.toRadians(0),   // View from south
      pitch: Cesium.Math.toRadians(-25),   // Look down at 25 degrees
      roll: 0,
    },
    duration: 2,
  });

  // Use lookAt for more control
  setTimeout(() => {
    const offset = new Cesium.HeadingPitchRange(
      Cesium.Math.toRadians(0),
      Cesium.Math.toRadians(-25),
      distance
    );
    viewer.camera.lookAt(centerPoint, offset);
    viewer.camera.lookAtTransform(Cesium.Matrix4.IDENTITY);
  }, 2100);
}
